import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Images, ArrowClockwise } from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { PromoSelect } from "@/promotions/components/PromoSelect";
import { EmptyState } from "@/promotions/components/EmptyState";
import { StatusBadge } from "@/promotions/components/StatusBadge";
import { CardsSkeleton } from "@/promotions/components/Skeleton";
import { MediaImage } from "../components/MediaImage";
import { useCreativeData, mediaUrl } from "../useCreative";

export default function Assets() {
  const { assets, mediaMap, loading, reload } = useCreativeData();
  const nav = useNavigate();
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");

  const filtered = useMemo(
    () => assets.filter((a) => (!kind || a.kind === kind) && (!status || a.status === status)),
    [assets, kind, status],
  );

  return (
    <div className="space-y-8" data-testid="creative-assets">
      <PromoPageHeader
        overline="Creative Studio"
        title="Assets"
        description="Review, remix and publish generated creative. Open any asset for its full operational detail."
        actions={
          <button onClick={reload} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid="assets-refresh">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-3" data-testid="assets-filters">
        <PromoSelect
          value={kind} onChange={setKind} placeholder="All kinds" className="w-40"
          options={[{ value: "", label: "All kinds" }, { value: "image", label: "Image" }, { value: "video", label: "Video" }, { value: "audio", label: "Audio" }]}
          testId="filter-kind"
        />
        <PromoSelect
          value={status} onChange={setStatus} placeholder="All statuses" className="w-44"
          options={[{ value: "", label: "All statuses" }, { value: "draft", label: "Draft" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }, { value: "published", label: "Published" }, { value: "archived", label: "Archived" }]}
          testId="filter-status"
        />
        <span className="text-sm text-muted-foreground" data-testid="assets-count">{filtered.length} asset{filtered.length === 1 ? "" : "s"}</span>
      </div>

      {loading ? (
        <CardsSkeleton count={8} />
      ) : filtered.length === 0 ? (
        <EmptyState Icon={Images} title="No assets found" description="Generate creative or adjust your filters." />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="assets-grid">
          {filtered.map((a) => (
            <button
              key={a.id} onClick={() => nav(`/creative/assets/${a.id}`)}
              className="group overflow-hidden rounded-lg border border-border bg-card text-left transition-colors hover:border-brand/40"
              data-testid={`asset-card-${a.id}`}
            >
              <div className="aspect-square w-full overflow-hidden">
                <MediaImage url={mediaUrl(mediaMap, a.media_asset_id, "thumbnail")} alt={a.operation} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </div>
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <span className="truncate text-xs capitalize text-muted-foreground">{a.kind} · {a.operation}</span>
                <StatusBadge status={a.status} dot={false} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
